import React, { useState, useEffect, useCallback } from 'react';
import { Box, Tabs, Tab, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { useFirebase } from '../../../context/FirebaseContext';
import { getAlphaStats } from '../../../api/alpha';
import WavesManagement from './WavesManagement';
import CodesManagement from './CodesManagement';
import WaitlistManagement from './WaitlistManagement';
import AlphaStatsPanel from './AlphaStatsPanel';
import logger from '../../../utils/logger';
import './AlphaAccessTab.css';

const AlphaAccessTab = () => {
    const { auth } = useFirebase();
    const [activeSubTab, setActiveSubTab] = useState(0);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await new Promise((resolve) => {
                getAlphaStats(auth, resolve);
            });
            if (response.error) {
                setError(response.error);
            } else {
                setStats(response);
            }
        } catch (err) {
            logger.error('Error loading alpha stats:', err);
            setError('Failed to load alpha statistics');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleSubTabChange = (event, newValue) => {
        setActiveSubTab(newValue);
    };

    const handleRefreshStats = () => {
        loadStats();
    };

    if (loading && !stats) {
        return (
            <Box className="alpha-loading">
                <CircularProgress />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Loading Alpha Access data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box className="alpha-access-tab">
            {/* Stats Overview Panel */}
            <AlphaStatsPanel stats={stats} onRefresh={handleRefreshStats} loading={loading} />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Sub-navigation */}
            <Paper className="alpha-sub-tabs-container">
                <Tabs
                    value={activeSubTab}
                    onChange={handleSubTabChange}
                    variant="fullWidth"
                    className="alpha-sub-tabs"
                >
                    <Tab label="Waves" />
                    <Tab label="Invite Codes" />
                    <Tab label="Waitlist" />
                </Tabs>
            </Paper>

            {/* Sub-tab Content */}
            <Box className="alpha-sub-content">
                {activeSubTab === 0 && (
                    <WavesManagement onUpdate={handleRefreshStats} />
                )}

                {activeSubTab === 1 && (
                    <CodesManagement onUpdate={handleRefreshStats} />
                )}

                {activeSubTab === 2 && (
                    <WaitlistManagement onUpdate={handleRefreshStats} />
                )}
            </Box>
        </Box>
    );
};

export default AlphaAccessTab;
